import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Action } from 'src/app/core/models/action.model';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { notesAllowProceed } from 'src/app/core/utils/kyc-postpone.util';

export interface PollingConfig {
  actionId: string;
  doneActionId?: string; // ✅ Para KYC, usar done_action_id que é único
  claimId?: string; // ✅ Para filtrar por claim específico
  actionType: 'KYC' | 'SIGNATURE' | 'QUESTIONNAIRE';
  initialDelay: number; // Ex: 2000ms
  maxDelay: number; // Ex: 30000ms (30s)
  backoffMultiplier: number; // Ex: 1.5 (exponencial)
  maxAttempts?: number; // Ex: 20 tentativas
}

export interface PollingResult {
  status: string;
  action: Action;
  completed: boolean;
  attempt: number;
  nextDelay: number;
  /**
   * The authoritative `cp_kyc_check.stage` for KYC actions (undefined for other action
   * types, or when the check couldn't be read). SomosKycV1 drives its completion-step UI
   * off this on every tick; other providers ignore it.
   */
  checkStage?: string;
}

/**
 * Thrown when the poll runs out of attempts before the action resolved. This is
 * NOT a failure — the provider simply hasn't finished within the window — so
 * consumers must branch on the type (not a substring of the message) to land on
 * a calm "still reviewing" terminal instead of a hard error.
 */
export class PollingExhaustedError extends Error {
  /** Stable discriminant, robust to bundling/`instanceof` gaps across module copies. */
  readonly code = 'POLLING_EXHAUSTED' as const;
  constructor(
    public readonly attempts: number,
    public readonly actionId: string,
  ) {
    super(`Max polling attempts (${attempts}) reached for action ${actionId}`);
    this.name = 'PollingExhaustedError';
    // Restore the prototype chain (TS target < ES2015 breaks `instanceof` otherwise).
    Object.setPrototypeOf(this, PollingExhaustedError.prototype);
  }
}

@Injectable({
  providedIn: 'root',
})
export class ActionPollingService {
  constructor(
    private claimsService: ClaimsService,
    private kycService: KycService
  ) {}

  /**
   * Inicia polling exponencial para verificar status de uma action
   * @param config Configuração do polling
   * @returns Observable que emite resultado a cada checagem
   */
  poll(config: PollingConfig): Observable<PollingResult> {
    return new Observable((observer) => {
      let attempt = 0;
      let currentDelay = config.initialDelay;
      let timeoutId: any;

      console.log('[GUIDE_POLLING] Starting polling', {
        actionId: config.actionId,
        doneActionId: config.doneActionId,
        claimId: config.claimId,
        actionType: config.actionType,
        initialDelay: config.initialDelay,
        maxDelay: config.maxDelay,
        backoffMultiplier: config.backoffMultiplier,
        maxAttempts: config.maxAttempts,
      });

      const checkStatus = async () => {
        attempt++;

        console.log('[GUIDE_POLLING] Checking status', {
          attempt,
          actionId: config.actionId,
          doneActionId: config.doneActionId,
          claimId: config.claimId,
          currentDelay,
        });

        try {
          // Busca action atualizada do backend
          // Para KYC, usa done_action_id que é único por execução
          const lookupId = config.doneActionId || config.actionId;
          const useDoneId = !!config.doneActionId;
          
          const action = await this.getActionStatus(lookupId, useDoneId, config.claimId);

          console.log('[GUIDE_POLLING] Action fetched', {
            attempt,
            actionId: config.actionId,
            doneActionId: config.doneActionId,
            claimId: config.claimId,
            usedDoneId: useDoneId,
            status: action.ac_status,
          });

          const result = await this.evaluateStatus(
            action,
            config.actionType,
            attempt,
            currentDelay
          );

          console.log('[GUIDE_POLLING] Status evaluated', {
            attempt,
            actionId: config.actionId,
            status: result.status,
            completed: result.completed,
          });

          observer.next(result);

          // Se completou com sucesso, para o polling
          if (result.completed) {
            console.log('[GUIDE_POLLING] Polling completed successfully', {
              actionId: config.actionId,
              finalStatus: result.status,
              totalAttempts: attempt,
            });
            observer.complete();
            return;
          }

          // ✅ Se status é DONE mas completed=false (KYC com passed=false), também para
          if (result.status === 'DONE' && !result.completed) {
            console.log('[GUIDE_POLLING] Polling stopped - action done but validation failed', {
              actionId: config.actionId,
              status: result.status,
              totalAttempts: attempt,
            });
            observer.complete();
            return;
          }

          // Se status é RESUBMISSION, trata igual DONE com passed=false
          if (result.status === 'RESUBMISSION') {
            console.log('[GUIDE_POLLING] Polling stopped - resubmission required (treated as failed)', {
              actionId: config.actionId,
              status: result.status,
              totalAttempts: attempt,
            });
            observer.complete();
            return;
          }

          // 🇧🇷 SERPRO name gate: pausado aguardando o usuário aceitar o nome oficial.
          // Para o polling para o subscriber abrir o modal de confirmação.
          if (result.status === 'AWAITING_NAME_CONFIRMATION') {
            console.log('[GUIDE_POLLING] Polling stopped - awaiting SERPRO name confirmation', {
              actionId: config.actionId,
              totalAttempts: attempt,
            });
            observer.complete();
            return;
          }

          // Se status é FAILED/REJECTED/ERROR/CANCELED, também para
          if (
            result.status === 'FAILED' ||
            result.status === 'REJECTED' ||
            result.status === 'ERROR' ||
            result.status === 'CANCELED'
          ) {
            console.log('[GUIDE_POLLING] Polling stopped - action failed', {
              actionId: config.actionId,
              status: result.status,
              totalAttempts: attempt,
            });
            observer.complete();
            return;
          }

          // Verifica se atingiu máximo de tentativas
          if (config.maxAttempts && attempt >= config.maxAttempts) {
            console.warn('[GUIDE_POLLING] Max attempts reached', {
              actionId: config.actionId,
              maxAttempts: config.maxAttempts,
              lastStatus: result.status,
            });
            observer.error(
              new PollingExhaustedError(config.maxAttempts, config.actionId)
            );
            return;
          }

          // Calcula próximo delay (exponential backoff)
          currentDelay = Math.min(
            currentDelay * config.backoffMultiplier,
            config.maxDelay
          );

          console.log('[GUIDE_POLLING] Scheduling next check', {
            attempt,
            actionId: config.actionId,
            nextDelay: currentDelay,
          });

          // Agenda próxima tentativa
          timeoutId = setTimeout(checkStatus, currentDelay);
        } catch (error) {
          console.error('[GUIDE_POLLING] Error during polling', {
            attempt,
            actionId: config.actionId,
            error: error instanceof Error ? error.message : String(error),
          });
          observer.error(error);
        }
      };

      // Inicia primeira checagem
      checkStatus();

      // Cleanup function
      return () => {
        if (timeoutId) {
          console.log('[GUIDE_POLLING] Polling cancelled', {
            actionId: config.actionId,
            attempt,
          });
          clearTimeout(timeoutId);
        }
      };
    });
  }

  /**
   * Avalia o status da action e determina se está completa
   */
  private async evaluateStatus(
    action: Action,
    type: string,
    attempt: number,
    nextDelay: number
  ): Promise<PollingResult> {
    const status = action.ac_status || 'PENDING';

    let completed = false;
    // Threaded onto every returned result so SomosKycV1 can sync its steps off the real stage.
    // Stays undefined for non-KYC actions and when the check can't be read.
    let checkStage: string | undefined;

    switch (type) {
      case 'KYC': {
        // The kyc_check STAGE is the authoritative signal — drive off it directly. The action
        // view's `ac_status` can be stale or missing (notably for shared testbench actions whose
        // done_action_id linkage is broken), so we only fall back to it when the check itself
        // can't be read. `done_action_id` here is the submitted kyc_check id.
        const kycCheck = action.done_action_id
          ? await this.kycService.getById(action.done_action_id).catch(() => null)
          : null;
        const stage = (kycCheck as any)?.stage as string | undefined;
        checkStage = stage;

        // 🇧🇷 SERPRO name gate: paused before the DocumentCheck, awaiting the user to accept the
        // official SERPRO name.
        if (stage === 'AWAITING_NAME_CONFIRMATION') {
          return { status: 'AWAITING_NAME_CONFIRMATION', action, completed: false, attempt, nextDelay, checkStage };
        }
        if (stage === 'DONE') {
          return { status: 'DONE', action, completed: this.kycCheckAllowsProceed(kycCheck), attempt, nextDelay, checkStage };
        }
        if (stage === 'RESUBMISSION') {
          return { status: 'RESUBMISSION', action, completed: false, attempt, nextDelay, checkStage };
        }
        if (stage === 'CANCELLED') {
          return { status: 'CANCELED', action, completed: false, attempt, nextDelay, checkStage };
        }
        if (stage === 'ERROR') {
          return { status: 'ERROR', action, completed: false, attempt, nextDelay, checkStage };
        }
        // In-progress (PRE_*/PROCESSING/PENDING) or the check couldn't be read → keep polling;
        // honor a DONE action status only as a fallback when the check stage is unavailable.
        completed = !stage && status === 'DONE' ? this.kycCheckAllowsProceed(kycCheck) : false;
        break;
      }

      case 'SIGNATURE':
        // Assinatura só está completa se status = SIGNED
        completed = status === 'SIGNED';
        break;

      case 'QUESTIONNAIRE':
        // Questionário aceita DONE ou SUBMITTED
        completed = status === 'DONE' || status === 'SUBMITTED';
        break;

      default:
        completed = false;
    }

    return {
      status,
      action,
      completed,
      attempt,
      nextDelay,
      checkStage,
    };
  }

  /**
   * Decide se um kyc_check já buscado libera o avanço: passed=true ou FAIL
   * adiável (notes.postpone_reason.canProceed — CPK-3620).
   */
  private kycCheckAllowsProceed(kycCheck: any): boolean {
    if (!kycCheck) {
      return false;
    }
    const passed = kycCheck.passed === true;
    const canProceed = notesAllowProceed(kycCheck.notes);
    return passed || canProceed;
  }

  /**
   * Valida se o KYC realmente passou verificando o cp_kyc_check
   */
  private async validateKycPassed(action: Action): Promise<boolean> {
    if (!action.done_action_id) {
      console.warn('[GUIDE_POLLING] KYC marked DONE but no done_action_id', {
        actionId: action.action_id,
      });
      return false;
    }

    try {
      const kycCheck = await this.kycService.getById(action.done_action_id);
      
      if (!kycCheck) {
        console.warn('[GUIDE_POLLING] KYC check not found', {
          actionId: action.action_id,
          kycCheckId: action.done_action_id,
        });
        return false;
      }

      const passed = kycCheck.passed === true;
      // CPK-3620: FAILED but postponable (notes.postpone_reason.canProceed) counts as done.
      const canProceed = notesAllowProceed((kycCheck as any).notes);

      console.log('[GUIDE_POLLING] KYC check validated', {
        actionId: action.action_id,
        kycCheckId: action.done_action_id,
        passed,
        canProceed,
        status: action.ac_status,
      });

      return passed || canProceed;
    } catch (error) {
      console.error('[GUIDE_POLLING] Error validating KYC check', {
        actionId: action.action_id,
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Busca status atualizado da action no backend
   * @param lookupId - ID para buscar (done_action_id ou action_id)
   * @param useDoneId - Se true, usa getActionByDoneId, senão usa getActionById
   * @param claimId - ID do claim (opcional nos dois casos; em getActionByDoneId, quando omitido,
   *   o backend resolve o claim a partir do done_action_id via rpc_get_action_by_done_id — CPK-4068)
   */
  private async getActionStatus(lookupId: string, useDoneId: boolean = false, claimId?: string): Promise<Action> {
    try {
      const action = useDoneId
        ? await this.claimsService.getActionByDoneId(lookupId, claimId)
        : await this.claimsService.getActionById(lookupId, claimId);

      if (!action) {
        // KYC done-id polling: the cpv_actions_complete row can be missing/stale — the view links
        // done_action_id only for some checks, and shared testbench actions accumulate stale rows.
        // Don't crash the poll; synthesize a minimal action so the KYC branch can read the check
        // directly by id (cp_kyc_check.stage is the authoritative source).
        if (useDoneId) {
          return { done_action_id: lookupId, ac_status: null } as unknown as Action;
        }
        throw new Error(`Action not found for action_id: ${lookupId}`);
      }

      return action;
    } catch (error) {
      console.error('[GUIDE_POLLING] Failed to fetch action', {
        lookupId,
        useDoneId,
        claimId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }
}
